import React, { memo, useEffect, useMemo, useState } from "react";
import * as S from "./styled";
import PT from "prop-types";
import { useSearchUrlGenerator } from "@/hooks/useSearchUrlGenerator";
import isempty from "lodash/isEmpty";
import sortBy from "lodash/sortBy";
import Animated from "@/components/Animated";
import { useNotify } from "@/hooks/useNotify";

const Specs = (props) => {
  const match = props.matchedPath;
  const activeSpec = props.query.spec || match?.params?.spec;
  const groupedSpecs = props.specs;
  const globalActiveGroupId = useMemo(() => {
    if (!activeSpec) return null;
    for (const groupId in groupedSpecs) {
      const specs = groupedSpecs[groupId]?.specs;
      if (isempty(specs)) return null;
      if (specs.some((spec) => spec._id === activeSpec)) {
        return groupId;
      }
    }
  }, [groupedSpecs, activeSpec]);
  useEffect(() => {
    if (globalActiveGroupId) {
      setActiveGroupId(globalActiveGroupId);
    }
  }, [globalActiveGroupId]);
  const [activeGroupId_, setActiveGroupId] = useState(globalActiveGroupId);
  const allGroups = useMemo(() => {
    return sortBy(
      Object.entries(groupedSpecs).map(([_id, { group }]) => ({
        _id,
        ...group,
      })),
      "priority",
    );
  }, [groupedSpecs]);
  const activeGroupId = activeGroupId_ || allGroups[0]._id;
  const specs = groupedSpecs[activeGroupId]?.specs;
  return (
    <S.Wrap>
      <S.GroupsWrap>
        {allGroups.map((group) => (
          <div
            onClick={() => setActiveGroupId(group._id)}
            role="button"
            css={`
              width: 100px;
              display: flex;
              flex-direction: column;
              cursor: pointer;
              align-items: center;
              margin-right: 15px;
              margin-bottom: 10px;
              text-align: center;
              @media (max-width: ${S.mobileBp}) {
                width: auto;
                flex-direction: row;
                margin-right: 0;
                text-align: left;
              }
            `}
            key={group._id}
          >
            <div
              style={{
                background:
                  activeGroupId === group._id
                    ? "var(--color-primary)"
                    : group.color || "var(--bg-touchable)",
              }}
              css={`
                --width: 50px;
                border-radius: 50%;
                width: var(--width);
                min-width: var(--width);
                height: var(--width);
                min-height: var(--width);
                margin-bottom: 10px;
                display: flex;
                align-items: center;
                justify-content: center;
                transition: background 0.25s ease;
                @media (max-width: ${S.mobileBp}) {
                  margin-bottom: 0;
                  margin-right: 15px;
                }
              `}
            >
              {group.icon && (
                <img
                  css={`
                    width: 24px;
                    height: 24px;
                    object-fit: contain;
                  `}
                  role="presentation"
                  src={group.icon}
                />
              )}
            </div>
            <span>{group.title}</span>
          </div>
        ))}
      </S.GroupsWrap>
      <S.SpecsWrap id="search-specs-list-head">
        <Animated
          disableInitialAnimation
          exitBeforeEnter
          itemKey={activeGroupId}
          visible
        >
          <S.Slider>
            {specs.map((spec) => (
              <Spec
                t={props.t}
                query={props.query}
                key={spec._id}
                spec={spec}
                isActive={spec._id === activeSpec}
                locationSlug={props.locationSlug}
              />
            ))}
          </S.Slider>
        </Animated>
      </S.SpecsWrap>
    </S.Wrap>
  );
};
Specs.displayName = "Specs";
Specs.propTypes = {
  query: PT.object.isRequired,
  specs: PT.object.isRequired,
  locationSlug: PT.string,
  matchedPath: PT.object,

  t: PT.func,
};

const Spec = (props) => {
  const { notifyError } = useNotify();
  const { desc, title, _id } = props.spec;
  const generateUrl = useSearchUrlGenerator();
  const loc = props.locationSlug;
  if (!loc) {
    return (
      <S.ButtonSlide
        onClick={() => {
          notifyError(props.t("common:search_form.no_city_err"));
          const $input = document.getElementById("master-search-city-input");
          if ($input) {
            $input.focus();
          }
        }}
      >
        <span>{title}</span>
      </S.ButtonSlide>
    );
  }
  return (
    <S.Slide
      $isActive={props.isActive}
      to={generateUrl({
        query: {
          ...props.query,
        },
        params: { spec: _id, loc },
      })}
      title={desc}
    >
      <span>{title}</span>
    </S.Slide>
  );
};

export default memo(Specs);
